// ============================================
// LEAD SERVICE - Database Operations
// ============================================

import { supabase, invokeEdgeFunction } from '../lib/supabase';
import type { FormData, Lead, CreateOrderResponse, VerifyPaymentRequest, VerifyPaymentResponse, 
  checkPaymentStatus, paymentStatusResponse } from '../types';

// ============================================
// LEAD CRUD OPERATIONS
// ============================================

export async function createLead(userId: string, formData: FormData): Promise<Lead> {
  const { personalDetails, currentSetup, usageDetails, technicalPreferences, futurePlans } = formData;
  
  // Get WhatsApp number
  const whatsappNumber = personalDetails.whatsappSameAsPhone 
    ? personalDetails.phone 
    : (personalDetails.whatsappNumber || personalDetails.phone);

  const leadData = {
    user_id: userId,
    
    // Personal Details
    name: personalDetails.name,
    email: personalDetails.email,
    phone: personalDetails.phone,
    whatsapp_number: whatsappNumber,
    location: personalDetails.location,
    age: personalDetails.age || null,
    occupation: personalDetails.occupation || null,
    
    // Current Setup
    has_current_device: currentSetup.hasCurrentDevice,
    current_device_type: currentSetup.currentDeviceType,
    current_device_brand: currentSetup.currentDeviceBrand || null,
    current_device_age: currentSetup.currentDeviceAge || null,
    current_device_condition: currentSetup.currentDeviceCondition,
    pain_points: currentSetup.painPoints || [],
    
    // Usage Details
    device_intent: usageDetails.deviceIntent,
    persona: usageDetails.persona,
    primary_usage: usageDetails.primaryUsage,
    secondary_usage: usageDetails.secondaryUsage || [],
    daily_hours: usageDetails.dailyHours,
    work_from_home: usageDetails.workFromHome,
    needs_portability: usageDetails.needsPortability,
    os_preference: usageDetails.osPreference,
    specific_software: usageDetails.specificSoftware || null,
    
    // Technical Preferences
    screen_size_preference: technicalPreferences.screenSizePreference,
    storage_needs: technicalPreferences.storageNeeds,
    brand_preference: technicalPreferences.brandPreference || [],
    brand_avoid: technicalPreferences.brandAvoid || [],
    priority_features: technicalPreferences.priorityFeatures || [],
    
    // Future Plans
    planned_activities: futurePlans.plannedActivities,
    budget_range: futurePlans.budgetRange,
    budget_flexibility: futurePlans.budgetFlexibility,
    urgency: futurePlans.urgency,
    expected_usage_years: futurePlans.expectedUsageYears,
    additional_notes: futurePlans.additionalNotes || null,
    
    // Payment (initial state)
    payment_status: 'pending',
  };

  const { data, error } = await supabase
    .from('leads')
    .insert(leadData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function getLeadById(leadId: string): Promise<Lead | null> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('id', leadId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }
  return data;
}

export async function getUserLeads(userId: string): Promise<Lead[]> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function updateLead(leadId: string, updates: Partial<Lead>): Promise<Lead> {
  const { data, error } = await supabase
    .from('leads')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', leadId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// ============================================
// PAYMENT OPERATIONS
// ============================================

export async function createPaymentOrder(leadId: string): Promise<CreateOrderResponse> {
  const response = await invokeEdgeFunction<CreateOrderResponse>('create-order', {
    lead_id: leadId,
  });
  
  if (!response || !response.order_id || !response.key_id) {
    throw new Error('Invalid response from payment server. Please try again.');
  }
  
  return response;
}

export async function verifyPayment(payload: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
  return invokeEdgeFunction<VerifyPaymentResponse>('verify-payment', payload as unknown as Record<string, unknown>);
}

export async function checkPaymentStatus(payload: checkPaymentStatus): Promise<paymentStatusResponse> {
  return invokeEdgeFunction<paymentStatusResponse>('check-payment-status', payload as unknown as Record<string, unknown>);
}

// ============================================
// HELPER FUNCTIONS
// ============================================

export async function getLatestPendingLead(userId: string): Promise<Lead | null> {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .eq('user_id', userId)
    .eq('payment_status', 'pending')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

export async function hasExistingPaidLead(userId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('payment_status', 'paid');

  if (error) throw error;
  return (count ?? 0) > 0;
}
